import { Popover, PopoverContent, PopoverTrigger } from "@/v5/components/ui/popover";
import { getRecipientOptions, type RecipientPickerOption } from "@/services/communications.service";
import { cn } from "@/v5/lib/utils";
import { Loader2, User } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface AuthorFilterPillProps {
  selected: Set<number>;
  onChange: (next: Set<number>) => void;
  className?: string;
}

export function AuthorFilterPill({ selected, onChange, className }: AuthorFilterPillProps) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<RecipientPickerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  // Track "have we already kicked off the fetch" via a ref so the effect can
  // depend on `open` alone. If we put `loading`/`loaded` in the deps the
  // effect re-runs the moment we call setLoading(true), the cleanup fires
  // before the promise resolves, and `cancelled = true` swallows the result.
  const fetchStartedRef = useRef(false);

  useEffect(() => {
    if (!open || fetchStartedRef.current) return;
    fetchStartedRef.current = true;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getRecipientOptions()
      .then((options) => {
        if (cancelled) return;
        setUsers(Array.isArray(options.users) ? options.users : []);
      })
      .catch(() => {
        if (cancelled) return;
        // Allow a retry on the next open after a failure.
        fetchStartedRef.current = false;
        setError("Could not load users.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      u.label.toLowerCase().includes(term) || (u.title ?? "").toLowerCase().includes(term),
    );
  }, [users, search]);

  const toggle = useCallback(
    (userId: number) => {
      const next = new Set(selected);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      onChange(next);
    },
    [selected, onChange],
  );

  const clearAll = useCallback(() => onChange(new Set()), [onChange]);
  const selectAllFiltered = useCallback(() => {
    const next = new Set(selected);
    for (const u of filteredUsers) {
      const id = Number(u.token);
      if (Number.isFinite(id) && id > 0) next.add(id);
    }
    onChange(next);
  }, [selected, onChange, filteredUsers]);

  const count = selected.size;
  const active = count > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
            active
              ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300"
              : "text-slate-500 hover:bg-indigo-50 hover:text-indigo-600",
            className,
          )}
        >
          <User className="h-3 w-3" />
          Author{count > 0 ? ` (${count})` : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users…"
            className="h-7 flex-1 rounded-md border border-slate-200 bg-white px-2 text-xs placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
          />
        </div>
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 text-xs">
          <button
            type="button"
            onClick={selectAllFiltered}
            disabled={filteredUsers.length === 0}
            className="font-medium text-blue-600 hover:underline disabled:cursor-not-allowed disabled:text-slate-300 disabled:no-underline"
          >
            Select all{search.trim() ? " matching" : ""}
          </button>
          <button
            type="button"
            onClick={clearAll}
            disabled={selected.size === 0}
            className="font-medium text-slate-500 hover:underline disabled:cursor-not-allowed disabled:text-slate-300 disabled:no-underline"
          >
            Clear
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading users…
            </div>
          )}
          {error && !loading && (
            <div className="px-3 py-4 text-xs text-red-600">{error}</div>
          )}
          {!loading && !error && filteredUsers.length === 0 && (
            <div className="px-3 py-4 text-xs text-slate-500">No users match.</div>
          )}
          {!loading && !error && filteredUsers.map((u) => {
            const id = Number(u.token);
            const checked = selected.has(id);
            return (
              <label
                key={u.token}
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(id)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                />
                <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                  <span className="truncate text-slate-700">{u.label}</span>
                  {u.title ? (
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                      {u.title}
                    </span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
